import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { type SkipWaitingResponse } from "@/shared/domain/sessions";

import { sessionQueryKeys } from "./sessionQueryKeys";

export interface UseSkipWaitingForVariables {
  sessionId: string;
  fileIds: string[];
}

/** Stop a session waiting for some of its files to be ingested. */
export function useSkipWaitingFor() {
  const { sessionService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<SkipWaitingResponse, Error, UseSkipWaitingForVariables>({
    mutationFn: ({ sessionId, fileIds }) =>
      sessionService.skipWaitingFor(sessionId, fileIds),
    onSuccess: ({ ingestion: status }) => {
      queryClient.setQueryData(
        sessionQueryKeys.ingestion(status.session_id),
        status,
      );
      // The record's ingestion count may have moved; its ingestion status
      // (keyed under it) was just set, so it is not refetched.
      void queryClient.invalidateQueries({
        queryKey: sessionQueryKeys.detail(status.session_id),
        exact: true,
      });
      void queryClient.invalidateQueries({
        queryKey: sessionQueryKeys.allIngestionSummaries(),
      });
    },
  });
}
